import propTypes from 'prop-types';
import React, {Component} from 'react';
import {connect} from 'react-redux';
import _ from 'lodash';
import {checkFileName} from 'MyAccount/AssetManager/ImageManager/helpers';
import {downsizeImage, checkImageSize} from 'MyAccount/ViewDesigns/helpers/uploadHelper';
import {getCurrentFolderImages, getUploadedFiles} from 'MyAccount/AssetManager/ImageManager/selectors';
import {
  changeFileStatusError,
  pushImageToBar,
  changeFileStatusSuccess,
} from 'MyAccount/ViewDesigns/redux/modules/images';
import UploadImageButton from 'shared/components/UploadImageButton';
import {
  MAX_SIZE,
  CANCELED_FILE_UPLOAD_STATUS,
  ERROR_FILE_UPLOAD_STATUS,
} from 'MyAccount/AssetManager/ImageManager/consts';

const irrelevantErrors = [201, 207]; // errors of changing file name by API, irrelevant to us

@connect(
  (state, props) => ({
    currentImages: getCurrentFolderImages(state, props),
    uploadedFiles: getUploadedFiles(state, props),
  }),
  {pushImageToBar, changeFileStatusError, changeFileStatusSuccess}
)
export default class UploadImageToS3Button extends Component {
  static contextTypes = {
    imageUtils: propTypes.object.isRequired,
  };

  uploadImages = files => {
    const {currentFolder, resourceType, uploadImagesDirectToEditor, integrated, type, pushImageToBar} = this.props;
    let directUpload = false;
    let currentFolderPath = currentFolder;
    if (currentFolderPath) {
      currentFolderPath = _.replace(currentFolder, /\\/gi, '/');
      currentFolderPath = currentFolderPath ? `/${currentFolderPath}/` : '/';
    }
    if (integrated && _.isFunction(uploadImagesDirectToEditor)) {
      if (type && (type !== 'images' || (type === 'images' && _.size(files) === 1))) {
        directUpload = true;
      }
    }
    _.map(files, file => {
      const fileNameChecked = this.checkFileName(file.name);
      checkImageSize(file, ({width, height}) => {
        if (file.type !== 'image/gif' && (width > MAX_SIZE || height > MAX_SIZE)) {
          pushImageToBar({name: fileNameChecked, currentFolder: currentFolderPath, resourceType});
          downsizeImage(
            {
              file,
              maxSize: MAX_SIZE,
            },
            resizedImage => {
              let newFile = new File([resizedImage], fileNameChecked, {
                type: file.type,
              });
              if (resizedImage.size > file.size) {
                newFile = file;
              }
              if (directUpload) {
                uploadImagesDirectToEditor([newFile]);
              }
              this.uploadFile(currentFolderPath, newFile, fileNameChecked, true);
            }
          );
        } else {
          if (directUpload) {
            uploadImagesDirectToEditor([file]);
          }
          this.uploadFile(currentFolderPath, file, fileNameChecked);
        }
      });
    });
  };

  handleResponse = (name, data) => {
    const {onFileUpload, changeFileStatusError, changeFileStatusSuccess} = this.props;
    const errorMessage = _.get(data, 'error.message');
    if (errorMessage && _.find(irrelevantErrors, data.error.number) === -1) {
      console.log('error uploading to s3', errorMessage);
    } else if (_.isFunction(onFileUpload)) {
      onFileUpload(
        {
          imageName: name,
          url: data.imageUrl ? data.imageUrl : data.currentFolder.url + encodeURIComponent(data.fileName),
        },
        response => {
          const {success, resourceType, error} = response;
          if (success) {
            changeFileStatusSuccess({name, resourceType});
          } else {
            changeFileStatusError({name, resourceType, error, status: ERROR_FILE_UPLOAD_STATUS});
          }
        }
      );
    }
  };

  uploadFile = (currentFolderPath, file, fileNameChecked, imageTooBig = false, retry = false) => {
    const {resourceType, changeFileStatusError} = this.props;
    this.context.imageUtils.doUploadFiles(
      {
        path: currentFolderPath,
        file,
        fileNameChecked,
        imageTooBig,
        resourceType,
        retry,
        retryUpload: () => this.uploadFile(currentFolderPath, file, fileNameChecked, imageTooBig, true),
        cancelUpload: () =>
          changeFileStatusError({name: fileNameChecked, resourceType, status: CANCELED_FILE_UPLOAD_STATUS}),
      },
      response => this.handleResponse(fileNameChecked, response)
    );
  };

  checkFileName = filename => {
    const {currentImages, uploadedFiles} = this.props;
    // check in upload images
    let fileNameChecked = checkFileName(currentImages, filename);
    // check in error upload in status bar
    fileNameChecked = checkFileName(uploadedFiles, fileNameChecked);
    return fileNameChecked;
  };

  uploadImagesFromUrls = (urls, retry = false) => {
    const {currentImages, uploadedFiles, currentFolder, resourceType, changeFileStatusError} = this.props;
    const checkNameArr = [];
    let path = currentFolder;
    if (path) {
      path = `/${_.replace(currentFolder, /\\/gi, '/')}/`;
    }
    _.map(urls, url => {
      const newUrl = url.src;
      let name = url.name;
      if (!_.endsWith(name, '.jpg') || !_.endsWith(name, '.png') || !_.endsWith(name, '.jpeg')) {
        name = name
          .split('/')
          .pop()
          .split('#')[0]
          .split('?')[0];
      }
      if (!retry) {
        const fileNameSplited = _.split(name, '.');
        let i = 0;
        while (
          name !== checkFileName(checkNameArr, name) ||
          name !== checkFileName(currentImages, name) ||
          name !== checkFileName(uploadedFiles, name)
        ) {
          if (_.size(fileNameSplited) === 1) {
            name = `${fileNameSplited[0]}(${i})`;
          } else {
            const fileExt = _.last(fileNameSplited);
            name = `${_.join(_.dropRight(fileNameSplited), '.')}(${i}).${fileExt}`;
          }
          i += 1;
        }
      }
      checkNameArr.push({name});
      this.context.imageUtils.doUploadFilesFromUrl(
        {
          newUrl,
          path,
          name,
          retry,
          resourceType,
          cancelUpload: () => changeFileStatusError({name: name, resourceType, status: CANCELED_FILE_UPLOAD_STATUS}),
          retryUpload: () => this.uploadImagesFromUrls([url], true),
        },
        response => this.handleResponse(name, response)
      );
    });
  };

  render() {
    const {integrated, openIntegratedUploadFromUrlDialog, buttonLabel, uniqueIdForUploadDialog, id} = this.props;
    return (
      <UploadImageButton
        id={id}
        uploadImages={this.uploadImages}
        uploadImagesFromUrl={this.uploadImagesFromUrls}
        openCustomUploadFromUrlDialog={integrated ? openIntegratedUploadFromUrlDialog : undefined}
        buttonLabel={buttonLabel}
        uniqueIdForUploadDialog={uniqueIdForUploadDialog}
      />
    );
  }
}
